import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MemberAvatar } from '@/components/clubs'
import { Button } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { useAddComment, useDeleteComment, useEntryComments } from '@/hooks/challenges'
import type { ChallengeEntry, ClubMember } from '@/types'

interface EntryCommentsProps {
  entry: ChallengeEntry
  members: Map<string, ClubMember>
  canModerate: boolean
}

/** Comment thread on one member's pick */
export function EntryComments({ entry, members, canModerate }: EntryCommentsProps) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { data: comments = [], isLoading } = useEntryComments(entry.id)
  const addComment = useAddComment()
  const deleteComment = useDeleteComment()
  const [body, setBody] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!body.trim()) return
    await addComment.mutateAsync({ entry, body })
    setBody('')
  }

  const timeFormat = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div className="space-y-3 rounded-md bg-muted/40 p-3">
      {isLoading ? (
        <div className="h-8 animate-pulse rounded bg-muted" />
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('challenges.comments.empty')}</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((comment) => {
            const author = members.get(comment.user_id)
            return (
              <li key={comment.id} className="flex gap-2">
                <MemberAvatar
                  userId={comment.user_id}
                  name={author?.display_name ?? '?'}
                  avatarUrl={author?.avatar_url}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {author?.display_name ?? t('challenges.formerMember')}
                    </span>{' '}
                    · {timeFormat.format(new Date(comment.created_at))}
                  </p>
                  <p className="whitespace-pre-wrap break-words text-sm text-foreground">
                    {comment.body}
                  </p>
                </div>
                {(comment.user_id === user?.id || canModerate) && (
                  <button
                    type="button"
                    className="shrink-0 self-start text-xs text-muted-foreground hover:text-error"
                    onClick={() =>
                      deleteComment.mutate({ comment, challengeId: entry.challenge_id })
                    }
                    aria-label={t('common.delete')}
                  >
                    ✕
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <form onSubmit={submit} className="flex gap-2">
        <input
          value={body}
          maxLength={1000}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('challenges.comments.placeholder')}
          aria-label={t('challenges.comments.placeholder')}
          className="h-9 min-w-0 flex-1 rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button type="submit" size="sm" disabled={!body.trim() || addComment.isPending}>
          {t('challenges.comments.send')}
        </Button>
      </form>
    </div>
  )
}
